"use client";

import * as React from "react";
import { useRef, useState } from "react";

import { cn } from "../lib/utils";
import { ButtonProps, buttonVariants } from "../ui/button";
import { ComboboxItems, useCombobox } from "./combobox";
import { Command } from "./command";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./hover-card";
import { Icon } from "./icon";
import { Input } from "./input";

interface PaginationProps extends React.ComponentProps<typeof PaginationRoot> {
  boundaries?: number;
  children?: React.ReactNode;
  currentPage: number;
  /** Disables all pagination controls (nav buttons, page links, and jump input). */
  disabled?: boolean;
  hasFirstButton?: boolean;
  hasJump?: boolean;
  hasLastButton?: boolean;
  hasNextButton?: boolean;
  hasPreviousButton?: boolean;
  onPageChange: (page: number) => void;
  siblings?: number;
  startingPage?: number;
  totalPages: number;
}

const getPaginationMethods = ({
  currentPage,
  setCurrentPage,
  startingPage,
  totalPages,
}: {
  currentPage: number;
  setCurrentPage: PaginationProps["onPageChange"];
  startingPage: number;
  totalPages: number;
}) => ({
  first: (e: React.MouseEvent<unknown, MouseEvent>) => {
    e.preventDefault();

    setCurrentPage(startingPage);
  },
  last: (e: React.MouseEvent<unknown, MouseEvent>) => {
    e.preventDefault();

    setCurrentPage(totalPages);
  },
  next: (e: React.MouseEvent<unknown, MouseEvent>) => {
    e.preventDefault();

    if (currentPage + 1 > totalPages) return;
    setCurrentPage(currentPage + 1);
  },
  previous: (e: React.MouseEvent<unknown, MouseEvent>) => {
    e.preventDefault();

    if (currentPage - 1 < startingPage) return;
    setCurrentPage(currentPage - 1);
  },
});

/**
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/pagination
 */
const Pagination = ({
  boundaries = 1,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- lint-backlog: binding kept on purpose (prop API, rest-spread exclusion or declaration-merge signature); removing it changes the API
  children,
  currentPage,
  disabled = false,
  hasFirstButton = true,
  hasJump = true,
  hasLastButton = true,
  hasNextButton = true,
  hasPreviousButton = true,
  onPageChange: setCurrentPage,
  siblings = 1,
  startingPage = 1,
  totalPages,
  ...props
}: PaginationProps) => {
  const jumpInputRef = useRef<HTMLInputElement>(null);
  const [jumptInputValue, setJumpInputValue] = useState<string>("");
  const [open, setOpen] = useState<boolean>(false);
  const combobox = useCombobox({
    options: new Array(Math.max(totalPages - startingPage + 1, 0))
      .fill(0)
      .map((_, i) => (i + startingPage).toString()),
    selected: [currentPage.toString()],
    setSelected: (value) => {
      setCurrentPage(Number(value));
    },
    allowDeselect: false,
    hasSearch: false,
    isMultiselect: false,
    showCheck: false,
    sorting: "none",
  });

  if (totalPages < 1) return;

  if (boundaries < 0) {
    throw new Error("Error: boundaries must be greater than or equal to zero.");
  }
  if (siblings < 0) {
    throw new Error("Error: siblings must be greater than or equal to zero.");
  }
  if (startingPage < 1) {
    throw new Error("Error: startingPage must be greater than zero.");
  }
  if (startingPage > totalPages) {
    throw new Error(
      "Error: startingPage must be less than or equal to totalPages.",
    );
  }
  if (currentPage < startingPage || currentPage > totalPages) {
    throw new Error(
      "Error: currentPage must be between startingPage and totalPages.",
    );
  }

  const { first, last, next, previous } = getPaginationMethods({
    currentPage,
    setCurrentPage,
    startingPage,
    totalPages,
  });

  const guardClick =
    (handler: (e: React.MouseEvent<unknown, MouseEvent>) => void) =>
    (e: React.MouseEvent<unknown, MouseEvent>) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      handler(e);
    };

  const handleJumpInputBlur = () => {
    setOpen(false);
    jumpInputRef.current?.blur();

    if (
      jumptInputValue !== "" &&
      Number(jumptInputValue) >= startingPage &&
      Number(jumptInputValue) <= totalPages
    ) {
      setCurrentPage(Number(jumptInputValue));
    }

    setJumpInputValue("");
  };

  const isJumpInputFocused = document.activeElement === jumpInputRef.current;

  const isFirstPage = currentPage === startingPage;
  const isLastPage = currentPage === totalPages;
  const currentPageRenderRangeStart = currentPage - siblings;
  const currentPageRenderRangeEnd = currentPage + siblings;

  const renderedPages: (number | "ellipsis")[] = [];

  for (let i = startingPage; i <= totalPages; i++) {
    if (i >= currentPageRenderRangeStart && i <= currentPageRenderRangeEnd) {
      // Current page and siblings
      renderedPages.push(i);
    } else if (
      // Boundary pages
      (i >= startingPage && i <= startingPage + boundaries - 1) ||
      i >= totalPages - boundaries + 1
    ) {
      renderedPages.push(i);
    } else if (
      boundaries > 0 &&
      // Show additional pages when selection is near a boundary to avoid
      // layout shift. Skipped when boundaries = 0 — no pinned edge pages.

      // Current page is near start boundary
      ((currentPage <= startingPage + boundaries + siblings + 1 &&
        // Show up to furthest possible sibling page
        i <= startingPage + boundaries + siblings * 2 + 1) ||
        // Current page is near end boundary
        (currentPage >= totalPages - siblings - boundaries - 1 &&
          // Show up to furthest possible sibling page
          i >= totalPages - siblings * 2 - boundaries - 1))
    ) {
      renderedPages.push(i);
    } else if (
      renderedPages[renderedPages.length - 1] !== "ellipsis" &&
      boundaries > 0
    ) {
      renderedPages.push("ellipsis");
    }
  }

  return (
    <PaginationRoot {...props}>
      {/* <PaginationContent> */}
      <PaginationContent className="[&>*]:flex-initial">
        {hasFirstButton && (
          <PaginationItem>
            <PaginationFirst
              disabled={disabled || isFirstPage}
              onClick={guardClick(first)}
            />
          </PaginationItem>
        )}
        {hasPreviousButton && (
          <PaginationItem>
            <PaginationPrevious
              disabled={disabled || isFirstPage}
              onClick={guardClick(previous)}
            />
          </PaginationItem>
        )}

        {renderedPages.map((page, i) => {
          if (page === "ellipsis") {
            return (
              <PaginationItem key={i}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          if (page === currentPage) {
            const options: string[] = [];

            for (let i = startingPage; i <= totalPages; i++) {
              options.push(i.toString());
            }

            return (
              <PaginationItem
                className={cn(page === currentPage && "bg-card")}
                key={i}
              >
                {!hasJump ? (
                  <PaginationLink
                    disabled={disabled}
                    isActive={page === currentPage}
                    onClick={(e) => {
                      e.preventDefault();
                    }}
                  >
                    {page}
                  </PaginationLink>
                ) : (
                  <HoverCard open={disabled ? false : open}>
                    <HoverCardTrigger asChild>
                      <Input
                        className={cn(
                          buttonVariants({
                            variant: "outline",
                            disabled,
                          }),
                          "min-w-0 cursor-pointer pl-2.5 font-normal",
                        )}
                        disabled={disabled}
                        onBlur={handleJumpInputBlur}
                        onChange={(e) => setJumpInputValue(e.target.value)}
                        onFocus={() => {
                          if (disabled) return;
                          setOpen(true);
                          setJumpInputValue("");
                        }}
                        onKeyDown={(e) => {
                          if (disabled) return;
                          if (e.key === "Enter") {
                            handleJumpInputBlur();
                          }
                          if (e.key === "Escape") {
                            handleJumpInputBlur();
                          }
                        }}
                        ref={jumpInputRef}
                        sectionRight={
                          <Icon icon="chevronDown" size="!xs" weight="bold" />
                        }
                        style={{
                          width: `calc(47px + ${
                            (jumptInputValue || currentPage).toString().length *
                            7
                          }px)`,
                        }}
                        value={
                          // Avoid old page number flash when changing pages
                          // due to a delay in updating value with useEffect.
                          isJumpInputFocused
                            ? jumptInputValue
                            : currentPage.toString()
                        }
                      />
                    </HoverCardTrigger>

                    <HoverCardContent className="w-[--radix-hover-card-trigger-width] p-0">
                      <Command defaultValue={currentPage.toString()}>
                        <ComboboxItems
                          classes={{
                            dropdownItem: "justify-center",
                          }}
                          store={combobox}
                        />
                      </Command>
                    </HoverCardContent>
                  </HoverCard>
                )}
              </PaginationItem>
            );
          }

          // Other pages
          return (
            <PaginationItem key={i}>
              <PaginationLink
                disabled={disabled}
                isActive={page === currentPage}
                onClick={(e) => {
                  e.preventDefault();
                  if (disabled) return;
                  setCurrentPage(page);
                }}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        {hasNextButton && (
          <PaginationItem>
            <PaginationNext
              disabled={disabled || isLastPage}
              onClick={guardClick(next)}
            />
          </PaginationItem>
        )}
        {hasLastButton && (
          <PaginationItem>
            <PaginationLast
              disabled={disabled || isLastPage}
              onClick={guardClick(last)}
            />
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationRoot>
  );
};
Pagination.displayName = "Pagination";

const PaginationRoot = ({
  className,
  ...props
}: React.ComponentProps<"nav">) => (
  <nav
    aria-label="pagination"
    className={cn("flex justify-center", className)}
    role="navigation"
    {...props}
  />
);
PaginationRoot.displayName = "PaginationRoot";

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<"ul">
>(({ className, ...props }, ref) => (
  <ul
    className={cn("flex flex-row items-center gap-1", className)}
    ref={ref}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<"li">
>(({ className, ...props }, ref) => (
  <li className={cn(className)} ref={ref} {...props} />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
} & ButtonProps &
  React.ComponentProps<"a">;

const PaginationLink = ({
  className,
  isActive,
  size,
  disabled,
  overrideClass,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? "page" : undefined}
    className={cn(
      buttonVariants({
        disabled,
        variant: isActive ? "outline" : "ghost",
        size: size ?? "icon",
        overrideClass,
      }),
      "cursor-pointer font-normal",
      className,
    )}
    {...{
      ...props,
      href: disabled ? undefined : props.href,
    }}
  />
);
PaginationLink.displayName = "PaginationLink";

const PaginationFirst = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to first page"
    className={cn("gap-1", className)}
    {...{
      title: "First",
      ...props,
    }}
  >
    <Icon icon="chevronDoubleLeft" size="!sm" weight="bold" />
    <span className="sr-only">First</span>
  </PaginationLink>
);
PaginationFirst.displayName = "PaginationFirst";

const PaginationPrevious = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to previous page"
    className={cn("gap-1", className)}
    {...{
      title: "Previous",
      ...props,
    }}
  >
    <Icon icon="chevronLeft" size="!sm" weight="bold" />
    <span className="sr-only">Previous</span>
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to next page"
    className={cn("gap-1", className)}
    {...{
      title: "Next",
      ...props,
    }}
  >
    <span className="sr-only">Next</span>
    <Icon icon="chevronRight" size="!sm" weight="bold" />
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationLast = ({
  className,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    aria-label="Go to last page"
    className={cn("gap-1", className)}
    {...{
      title: "Last",
      ...props,
    }}
  >
    <span className="sr-only">Last</span>
    <Icon icon="chevronDoubleRight" size="!sm" weight="bold" />
  </PaginationLink>
);
PaginationLast.displayName = "PaginationLast";

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span
    aria-hidden
    className={cn("flex h-9 w-9 items-center justify-center", className)}
    {...props}
  >
    <Icon icon="ellipsis" weight="bold" />
    <span className="sr-only">More pages</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

/** e.g. "1-25 of 75" — which rows of the total are on the current page. */
const PaginationRange = ({
  className,
  page,
  pageSize,
  totalItems,
  ...props
}: {
  page: number;
  pageSize: number;
  totalItems: number;
} & React.ComponentProps<"span">) => {
  if (totalItems <= 0) return null;

  const safePage = Math.min(
    Math.max(page, 1),
    Math.max(Math.ceil(totalItems / pageSize), 1),
  );

  const first = (safePage - 1) * pageSize + 1;
  const last = Math.min(safePage * pageSize, totalItems);

  return (
    <span className={cn("text-sm text-muted-foreground", className)} {...props}>
      {first}-{last} of {totalItems}
    </span>
  );
};
PaginationRange.displayName = "PaginationRange";

export {
  getPaginationMethods,
  Pagination,
  PaginationRange,
  // PaginationContent,
  // PaginationEllipsis,
  // PaginationFirst,
  // PaginationItem,
  // PaginationLast,
  // PaginationLink,
  // PaginationNext,
  // PaginationPrevious,
  // PaginationRoot,
  type PaginationProps,
};
